import axios, { AxiosError, InternalAxiosRequestConfig, AxiosHeaders, AxiosResponse } from "axios";

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:3944";

// Token refresh state — prevents multiple concurrent refresh attempts
let isRefreshing = false;
let refreshSubscribers: ((token: string) => void)[] = [];

function subscribeTokenRefresh(cb: (token: string) => void) {
  refreshSubscribers.push(cb);
}

function onTokenRefreshed(token: string) {
  refreshSubscribers.forEach((cb) => cb(token));
  refreshSubscribers = [];
}

// 🔥 Configuration des timeouts par type de requête
const getTimeoutForUrl = (url: string | undefined, method: string | undefined): number => {
  if (!url) return 30000; // Default
  
  const urlStr = url.toLowerCase();
  const methodStr = (method || 'get').toLowerCase();
  
  // 🔥 Timeouts spécifiques pour éviter les problèmes
  if (urlStr.includes('/withdrawal/check-eligibility')) {
    return 8000; // 8 secondes pour l'éligibilité
  }
  
  if (urlStr.includes('/balance-summary') || urlStr.includes('/dashboard/balances')) {
    return 30000; // 30 secondes pour les balances
  }
  
  if (urlStr.includes('/history')) {
    return 12000; // 12 secondes pour l'historique
  }
  
  if (methodStr === 'post' || methodStr === 'put' || methodStr === 'delete') {
    return 15000; // 15 secondes pour les mutations
  }
  
  return 30000; // Default: 30 secondes
};

// 🔥 Compteur pour le débogage
let requestCounter = 0;
const requestMap = new Map<number, { url: string; startTime: number; method: string }>();

// 🔥 Fonction utilitaire pour obtenir le dernier requestId de manière sécurisée
const getLastRequestId = (): number | undefined => {
  const keys = Array.from(requestMap.keys());
  return keys.length > 0 ? keys[keys.length - 1] : undefined;
};

// 🔥 Création de l'instance axios avec configuration optimisée
const api = axios.create({
  baseURL: API_URL,
  headers: {
    "Content-Type": "application/json",
    "Accept": "application/json",
  },
  // Timeout global élevé, mais sera écrasé par les intercepteurs
  timeout: 30000,
  withCredentials: true,
  // 🔥 Optimisations de performance
  maxRedirects: 5,
  decompress: true,
  transitional: {
    forcedJSONParsing: true,
    silentJSONParsing: true,
    clarifyTimeoutError: true,
  },
});

// 🔥 UN SEUL INTERCEPTEUR DE REQUÊTE (types corrigés)
api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const requestId = ++requestCounter;
  const startTime = Date.now();
  const url = config.url || '';
  const method = (config.method || 'GET').toUpperCase();
  
  // Stocker les infos pour le débogage
  requestMap.set(requestId, { url, startTime, method });
  
  // 🔥 Appliquer un timeout spécifique
  const timeout = getTimeoutForUrl(url, method);
  config.timeout = timeout;
  
  // 🔥 Ajouter le token d'authentification
  const token = localStorage.getItem("token");
  if (token) {
    // Initialiser les headers si nécessaire
    if (!config.headers) {
      config.headers = new AxiosHeaders();
    }
    // Ajouter l'autorization header
    config.headers.set('Authorization', `Bearer ${token}`);
  }
  
  return config;
}, (error) => {
  console.error("❌ [API Request Setup Error]", error);
  return Promise.reject(error);
});

// 🔥 INTERCEPTEUR DE RÉPONSE UNIFIÉ
api.interceptors.response.use(
  (response) => {
    const requestId = getLastRequestId();
    const requestInfo = requestId ? requestMap.get(requestId) : null;
    const endTime = Date.now();
    
    if (requestInfo && requestId) { // Vérifier que requestId n'est pas undefined
      const duration = endTime - requestInfo.startTime;
      
      // 🔥 Nettoyer la map
      requestMap.delete(requestId);
      
    }
    
    return response;
  },
  async (error: AxiosError) => {
    const requestId = getLastRequestId();
    const requestInfo = requestId ? requestMap.get(requestId) : null;
    const endTime = Date.now();
    const duration = requestInfo ? endTime - requestInfo.startTime : 0;
    
    // 🔥 Nettoyer la map même en cas d'erreur
    if (requestId !== undefined) {
      requestMap.delete(requestId);
    }
    
    // 🔥 Construction de l'objet d'erreur détaillé
    const errorDetails = {
      requestId,
      url: error.config?.url,
      method: error.config?.method?.toUpperCase(),
      code: error.code,
      message: error.message,
      status: error.response?.status,
      statusText: error.response?.statusText,
      duration: `${duration}ms`,
      timestamp: new Date().toISOString(),
    };
    
    // 🔥 Log structuré selon le type d'erreur
    if (error.code === 'ECONNABORTED') {
      console.group(`⏱️ [Timeout #${requestId}]`);
      console.log('URL:', errorDetails.url);
      console.log('Duration:', duration, 'ms (timeout dépassé)');
      console.log('Configured Timeout:', error.config?.timeout, 'ms');
      console.log('Method:', errorDetails.method);
      console.groupEnd();
      
      // Message utilisateur friendly
      error.message = `La requête a expiré après ${duration}ms. Le serveur est trop lent à répondre.`;
    } 
    else if (error.code === 'ERR_NETWORK' || error.message.includes('Network Error')) {
      console.error(`🌐 [Network Error #${requestId}] Impossible de joindre le serveur`);
      error.message = "Erreur réseau. Vérifiez votre connexion internet et réessayez.";
    } 
    else if (error.response?.status === 401) {
      const originalRequest = error.config;

      // Skip refresh for auth endpoints themselves
      const isAuthEndpoint = originalRequest?.url?.includes('/auth/login') ||
                             originalRequest?.url?.includes('/auth/refresh');

      if (!isAuthEndpoint && !(originalRequest as any)?._retry) {
        if (isRefreshing) {
          // Another refresh is in progress — queue this request
          return new Promise<AxiosResponse>((resolve) => {
            subscribeTokenRefresh((newToken: string) => {
              if (originalRequest?.headers) {
                originalRequest.headers.set('Authorization', `Bearer ${newToken}`);
              }
              resolve(api(originalRequest!));
            });
          });
        }

        (originalRequest as any)._retry = true;
        isRefreshing = true;

        try {
          const currentToken = localStorage.getItem("token");
          const refreshResponse = await axios.post(
            `${API_URL}/auth/refresh`,
            {},
            {
              headers: { Authorization: `Bearer ${currentToken}` },
              timeout: 10000,
            }
          );

          const newToken = refreshResponse.data.access_token;
          if (newToken) {
            localStorage.setItem("token", newToken);
            onTokenRefreshed(newToken);
            isRefreshing = false;

            if (originalRequest?.headers) {
              originalRequest.headers.set('Authorization', `Bearer ${newToken}`);
            }
            return api(originalRequest!);
          }
        } catch (refreshError) {
          isRefreshing = false;
          refreshSubscribers = [];
        }
      }

      console.warn(`🔒 [Unauthorized #${requestId}] Token invalide ou expiré — refresh failed`);

      localStorage.removeItem("token");
      localStorage.removeItem("user");

      window.dispatchEvent(new CustomEvent('auth:logout', {
        detail: { reason: 'token_expired' }
      }));
    }
    else if (error.response?.status === 404) {
      console.warn(`🔍 [Not Found #${requestId}] ${errorDetails.url}`);
    }
    else if (error.response?.status === 500) {
      console.error(`💥 [Server Error #${requestId}]`, {
        url: errorDetails.url,
        status: errorDetails.status,
        data: error.response?.data
      });
      error.message = "Erreur interne du serveur. Notre équipe technique en a été informée.";
    }
    else {
      // Erreur générique
      console.group(`❌ [API Error #${requestId}]`);
      console.log('Details:', errorDetails);
      if (error.response?.data) {
        console.log('Response Data:', error.response.data);
      }
      console.groupEnd();
    }
    
    // 🔥 Ajouter des métadonnées utiles à l'erreur
    (error as any).details = errorDetails;
    (error as any).requestId = requestId;
    (error as any).duration = duration;
    
    return Promise.reject(error);
  }
);

// 🔥 FONCTIONS UTILITAIRES
const apiUtils = {
  // Obtenir des statistiques sur les requêtes
  getRequestStats() {
    return {
      totalRequests: requestCounter,
      activeRequests: requestMap.size,
      requests: Array.from(requestMap.entries()).map(([id, info]) => ({
        id,
        ...info,
        duration: Date.now() - info.startTime
      }))
    };
  },
  
  // Vider le compteur (utile pour les tests)
  resetCounter() {
    requestCounter = 0;
    requestMap.clear();
  },
  
  // Vérifier si une URL est en cours de traitement
  isRequestPending(url: string): boolean {
    return Array.from(requestMap.values()).some(req => 
      req.url.includes(url)
    );
  },
  
  // Annuler toutes les requêtes en cours (pour les changements de page)
  cancelPendingRequests(message = 'Request cancelled') {
    // Pour annuler les requêtes axios, vous auriez besoin d'un CancelToken
    // Cette fonction est juste un placeholder
    console.log(`🛑 Cancelling ${requestMap.size} pending requests`);
    requestMap.clear();
  }
};

// 🔥 Gestionnaire d'erreurs global pour les promesses non attrapées
if (typeof window !== 'undefined') {
  window.addEventListener('unhandledrejection', (event) => {
    if (event.reason?.isAxiosError) {
      const error = event.reason as AxiosError;
      console.error('⚠️ [Unhandled API Promise]', {
        url: error.config?.url,
        method: error.config?.method,
        status: error.response?.status,
        message: error.message
      });
      // Empêcher le log par défaut du navigateur
      event.preventDefault();
    }
  });
}

// 🔥 EXPORT UNIQUE DES MODULES
export default api;
export { apiUtils };